import React, { useEffect, useState } from "react";
import { Box, Divider, Grid, CircularProgress } from "@mui/material";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import PatientDatas from "src/modules/medcard/patient-datas";
import { useAppSelector } from "src/app/hooks";
import { mcGeneralDiseaseService, McGeneralDiseaseItem } from "src/api/medcards/mcGeneralDiseaseService";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";
import { useTranslate } from "src/locales";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { parseISO } from "date-fns";

interface BloodPressureModalProps {
  open: boolean;
  onClose: () => void;
  patientId?: string | number;
  editData?: McGeneralDiseaseItem | null;
}

const BloodPressureModal: React.FC<BloodPressureModalProps> = ({
  open,
  onClose,
  patientId,
  editData,
}) => {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => (state as any).auth);

  const [operationDate, setOperationDate] = useState<Date | null>(new Date());
  const [operationUser, setOperationUser] = useState<string>("");
  const [systolicPressure, setSystolicPressure] = useState<string>("");
  const [diastolicPressure, setDiastolicPressure] = useState<string>("");
  const [pulse, setPulse] = useState<string>("");
  const [note, setNote] = useState<string>("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error">("success");
  const [loading, setLoading] = useState(false);

  // Kullanıcı adını otomatik olarak login durumundan al
  useEffect(() => {
    if (user?.username) {
      setOperationUser(user.username);
    }
  }, [user]);

  // Edit data varsa form'u doldur
  useEffect(() => {
    if (open && editData) {
      if (editData.name) {
        const systolicMatch = editData.name.match(/Büyük\s*:\s*(\d+)/i);
        const diastolicMatch = editData.name.match(/Küçük\s*:\s*(\d+)/i);
        const pulseMatch = editData.name.match(/Nabız\s*:\s*(\d+)/i);
        const noteMatch = editData.name.match(/Not\s*:\s*(.+)/i);
        setSystolicPressure(systolicMatch ? systolicMatch[1] : "");
        setDiastolicPressure(diastolicMatch ? diastolicMatch[1] : "");
        setPulse(pulseMatch ? pulseMatch[1] : "");
        // name'den parse edilen not varsa onu kullan, yoksa item.note veya item.privatenotes kullan
        const parsedNote = noteMatch ? noteMatch[1].trim() : "";
        setNote(parsedNote || editData.note || editData.privatenotes || "");
      } else {
        setNote(editData.note || editData.privatenotes || "");
      }
      if (editData.cuser) {
        setOperationUser(editData.cuser);
      }
      if (editData.pdate || editData.ctime) {
        try {
          const dateString = editData.pdate || editData.ctime;
          const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
          setOperationDate(date);
        } catch (e) {
          setOperationDate(new Date());
        }
      }
    }
  }, [open, editData]);

  // Modal açıldığında snackbar'ı temizle
  useEffect(() => {
    if (open) {
      setSnackbarOpen(false);
      setSnackbarMessage("");
    }
  }, [open]);

  // Modal kapandığında state'leri temizle
  useEffect(() => {
    if (!open) {
      setSystolicPressure("");
      setDiastolicPressure("");
      setPulse("");
      setNote("");
      setOperationDate(new Date());
      setSnackbarOpen(false);
      setSnackbarMessage("");
    }
  }, [open]);

  const handleSave = async () => {
    if (!patientId) {
      setSnackbarMessage(t("blood-pressure.errors.patient-id-not-found"));
      setSnackbarType("error");
      setSnackbarOpen(true);
      return;
    }

    // Büyük Tansiyon kontrolü
    const systolicValue = systolicPressure ? Number(systolicPressure) : 0;
    if (systolicValue <= 0) {
      setSnackbarMessage(t("blood-pressure.errors.systolic-pressure-not-entered"));
      setSnackbarType("error");
      setSnackbarOpen(true);
      return;
    }

    // Küçük Tansiyon kontrolü
    const diastolicValue = diastolicPressure ? Number(diastolicPressure) : 0;
    if (diastolicValue <= 0) {
      setSnackbarMessage(t("blood-pressure.errors.diastolic-pressure-not-entered"));
      setSnackbarType("error");
      setSnackbarOpen(true);
      return;
    }

    // Nabız kontrolü
    const pulseValue = pulse ? Number(pulse) : 0;
    if (pulseValue <= 0) {
      setSnackbarMessage(t("blood-pressure.errors.pulse-not-entered"));
      setSnackbarType("error");
      setSnackbarOpen(true);
      return;
    }

    // Edit modunda güncelleme
    if (editData) {
      if (!editData.id) {
        setSnackbarMessage(t("blood-pressure.errors.id-not-found"));
        setSnackbarType("error");
        setSnackbarOpen(true);
        return;
      }

      setLoading(true);
      try {
        await mcGeneralDiseaseService.updateBloodPressure({
          id: editData.id,
          greatbloodpressure: systolicValue,
          smallbloodpressure: diastolicValue,
          pulse: pulseValue,
          note: note || "",
        });

        setSnackbarMessage(t("blood-pressure.success.save-success"));
        setSnackbarType("success");
        setSnackbarOpen(true);
        setTimeout(() => {
          onClose();
        }, 1500);
      } catch (error: any) {
        console.error("Tansiyon takibi güncelleme hatası:", error);
        const errorMessage = error?.response?.data?.error || error?.error || t("blood-pressure.errors.save-error");
        setSnackbarMessage(errorMessage);
        setSnackbarType("error");
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Yeni kayıt
    setLoading(true);
    try {
      const requestData = {
        ptid: Number(patientId),
        greatbloodpressure: systolicValue,
        smallbloodpressure: diastolicValue,
        pulse: pulseValue,
        note: note || "",
      };

      await mcGeneralDiseaseService.addBloodPressure(requestData);
      setSnackbarMessage(t("blood-pressure.success.save-success"));
      setSnackbarType("success");
      setSnackbarOpen(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error: any) {
      console.error("Tansiyon takibi kaydetme hatası:", error);
      const errorMessage = error?.response?.data?.error || error?.error || t("blood-pressure.errors.save-error");
      setSnackbarMessage(errorMessage);
      setSnackbarType("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <OpenComponentDialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      hideHeader
      showPatientData={false}
    >
      <Box
        sx={{
          py: { xs: 2, sm: 3 },
          px: { xs: 1.5, sm: 2 },
          width: "100%",
          maxWidth: "100%",
        }}
      >
        {/* Başlık */}
        <Box
          sx={{
            width: "100%",
            backgroundColor: "#f4f7fe",
            borderBottom: "1px solid #e0e0e0",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "flex-start",
            py: 1.5,
            px: 2.5,
            mb: 2,
          }}
        >
          <ByzMuiTypography isTitle={true} sx={{ fontSize: 20, lineHeight: "22px", color: "#111827" }}>
            {t("blood-pressure.title")}
          </ByzMuiTypography>
        </Box>

        {/* Save / Close */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            gap: 1,
          }}
        >
          <ByzMuiSaveButton onClick={handleSave} disabled={loading} />
          <Box sx={{ flex: 1 }} />
          <ByzMuiCloseButton onClick={onClose} />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Hasta bilgisi kartı (grafiksiz) */}
        <Box sx={{ mb: 2 }}>
          <PatientDatas patientId={patientId} isCreateMedCard noMargin />
        </Box>

        {/* Form alanları */}
        <Box
          sx={{
            mt: 1,
            width: { xs: "100%", md: "60%" },
          }}
        >
          {/* Bölüm başlığı - Tansiyon Takibi */}
          <Box
            sx={{
              backgroundColor: "#F4F7FE",
              borderBottom: "1px solid #E0E0E0",
              mb: 1.5,
              py: 1,
            }}
          >
            <ByzMuiTypography sx={{ fontSize: 16 }}>
              {t("blood-pressure.title")}
            </ByzMuiTypography>
          </Box>

          <Grid container spacing={0}>
            {/* İşlem Tarihi */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("blood-pressure.operation-date")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiDateTimePicker
                    value={operationDate}
                    onChange={(val) => setOperationDate(val as Date | null)}
                    width="100%"
                    disabled
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* İşlem Yapan Kullanıcı */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("blood-pressure.operation-user")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiTextField
                    label="İşlem Yapan Kullanıcı"
                    value={operationUser}
                    onChange={(e) => setOperationUser(e.target.value)}
                    fullWidth
                    cantChange
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Büyük Tansiyon */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("blood-pressure.systolic-pressure")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiTextField
                    label="Giriniz"
                    placeholder={t("blood-pressure.systolic-pressure")}
                    value={systolicPressure}
                    onChange={(e) => setSystolicPressure(e.target.value)}
                    fullWidth
                    type="number"
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Küçük Tansiyon */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("blood-pressure.diastolic-pressure")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiTextField
                    label="Giriniz"
                    placeholder={t("blood-pressure.diastolic-pressure")}
                    value={diastolicPressure}
                    onChange={(e) => setDiastolicPressure(e.target.value)}
                    fullWidth
                    type="number"
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Nabız */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("blood-pressure.pulse")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiTextField
                    label="Giriniz"
                    placeholder={t("blood-pressure.pulse") || "Nabız"}
                    value={pulse}
                    onChange={(e) => setPulse(e.target.value)}
                    fullWidth
                    type="number"
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Not */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("blood-pressure.note")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiTextField
                    label="Giriniz"
                    placeholder={t("blood-pressure.note") || "Not"}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}

      {/* Snackbar */}
      <ByzMuiSnackBar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        title={snackbarType === "success" ? t("common.success") : t("common.error")}
        message={snackbarMessage}
        type={snackbarType}
      />
    </OpenComponentDialog>
  );
};

export default BloodPressureModal;
